import { Link } from "react-router-dom";
import { SectionTitle, FadeUp, HRule } from "./_shared";
import PatentTechCard from "./_partials/PatentTechCard";
import HudCorners from "./_partials/HudCorners";
import { ACTIVATION_URL } from "@/lib/links";

const PATENT_HERO =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/6iedbpw3_zmkfa1VYPGQAigIZ4CA13.png";

const TECHS = [
  {
    name: "The Multi-Tissue Biometric Engine",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/14v3cjjh_site%203.avif",
    sub: "A sensing system that looks at signals from skin, muscle, nerves, and blood vessels at the same time.",
    what:
      "Checks signals from four body layers at once. This creates a clearer picture of what is happening in real time. It brings together multiple biological signals so the system can observe the body more holistically.",
    how:
      "Different sensors pick up signals from different depths, then bring them together into one clear readout. The fusion step helps organize complex layered data into something easier to interpret.",
    sig:
      "Designed to identify emerging patterns and surface meaningful changes over time — supporting better monitoring and more personal, informed care.",
  },
  {
    name: "Wave-Harmonic Model — CardioSentry ICIT",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/dqgwuc03_cwzF_-SGb3NVE3V9mrgYZ.png",
    sub: "A new way to read heart and brain signals — looking at how the full P-QRS-T-U waveform fits together, not just separate spikes.",
    what:
      "Treats EKG and EEG traces as connected wave patterns. By analyzing P-QRS-T-U coupling, it scores 'Wave-Harmonic stability' — a real-time read of how cleanly your cardiovascular system is resonating.",
    how:
      "Signals are broken into smaller harmonic pieces, then reassembled to show where rhythm, timing, and recovery fit together. The engine is designed to flag loss of stability earlier than a one-off snapshot would.",
    sig:
      "Designed to let us treat exercise as a 'structural drug' — tracking your vascular resonance session over session as the system remodels, and surfacing rhythm, brain, and stress signals earlier than a single scan.",
  },
  {
    name: "The Multi-Tissue Photometric Engine",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/aoz69at7_site%204.avif",
    sub: "A simple, non-invasive light-based system that checks key body signals across different tissue layers in real time.",
    what:
      "Uses different light colors to look below the skin and read body signals in one quick scan. It is designed to gather information from several tissue depths at once.",
    how:
      "Light passes through the body, and smart analysis picks out the signal from each layer. The engine separates the returning light by wavelength and tissue depth.",
    sig:
      "Gives a real-time picture of body health without needles or samples. It can help track energy, inflammation, hydration, and oxygen levels.",
  },
  {
    name: "Body Composition & Metabolic Risk Engine™",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/oufr22gi_m46fb05y_tckDQ7XIFUwYtjoTpkSlh.png",
    sub: "Reads body composition and metabolic risk together — supporting earlier, more informed evaluation.",
    what:
      "Analyzes visceral fat, skeletal muscle quality, fat distribution, cellular hydration, metabolic age, sarcopenia risk, cardiometabolic risk, inflammatory burden, and insulin-resistance patterns.",
    how:
      "Fuses body-composition imaging with metabolic biomarkers and longitudinal trends — designed so subtle shifts surface earlier than standard panels alone.",
    sig:
      "Designed to support earlier intervention, more informed risk assessment, and precision tracking over time.",
  },
  {
    name: "Integrated Biological Age (IBA) 2.0",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/8b55qi9i_CqJfRge9P4wotqxdzdH_k.png",
    sub:
      "Evolution of the Biological Age Intelligence Engine — moving beyond the static methylation snapshot to a dynamic model built around the Biological Resilience Reserve (BRR), distinguishing Resilient from Fragile aging.",
    what:
      "IBA 2.0 weights three pillars into one age score — Epigenetic (40%), Vascular (35%), Behavioral (25%) — and detects six divergence patterns between them. New research challenges methylation-only approaches, so IBA 2.0 adds a Biological Resilience Reserve signal that separates Resilient from Fragile aging.",
    how:
      "Thirty-plus methylation clocks run in parallel for the epigenetic pillar, an AI vascular-age engine reads PPG resonance, and our BAPS-v3 score covers 8 behavioral domains. Outputs are weighted and reconciled — if patterns diverge (e.g. molecules look young but behavioral reserve is low), the engine automatically gates higher-tier interventions until the underlying gap is addressed.",
    sig:
      "Captures aging the way the body actually ages — across multiple biological axes — and routes resilience-aware findings through a multi-domain clinical framework with tier-based intervention activation and unified contraindication scanning. Surfaces actionable intervention windows that single-signal methylation tests miss entirely.",
    badge: "Patent Pending",
    href: "/biological-age",
    hrefLabel: "Explore IBA 2.0",
  },
  {
    name: "The Self-Regulation Console — BLS Engine",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/ytf7sar2_BLS%20Engine.png",
    sub:
      "A closed-loop wellness system built around a patent-pending bilateral stimulation engine — more than a relaxation app, engineered with the rigor of a clinical instrument.",
    what:
      "Two distinct modes for how you actually feel in the moment — a tap-based interface for active engagement, and a hands-free visual mode for receptive practice. Every session moves through a structured arc: safety screener → pre-check-in → bilateral session → post-check-in → grounding exit. Nothing is skipped.",
    how:
      "Built-in delta tracking captures pre- and post-session ratings so you can see exactly how your body and mind shifted. Optional wearable correlation — Apple Health, Oura, Whoop, Fitbit, or Garmin — aligns your session timestamps with physiological data for clinician review.",
    sig:
      "This is wellness coaching, not medical treatment — but it's wellness coaching engineered with the rigor of a clinical instrument. Designed for everyday self-regulation while remaining structured enough to integrate into a longitudinal care record.",
    badge: "Patent Pending · U.S. Utility in Preparation",
    docket: "Provisional 63/905,142 · Filed Oct 24, 2025",
  },
];

export default function PatentTech() {
  return (
    <section id="patent-tech" data-testid="patent-tech-section" className="relative py-24 md:py-36 bg-background border-y border-border">
      <div className="absolute inset-0 iw-grid-bg opacity-40" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8">
        <FadeUp className="text-center">
          <div
            data-testid="patent-tech-innovation-eyebrow"
            className="iw-overline mb-3"
          >
            Patent-Pending Technology
          </div>
          <SectionTitle className="mt-5 text-[clamp(2rem,4.5vw,3.5rem)]">
            Clinical intelligence technology —{" "}
            <em className="not-italic text-primary">right from your cell phone</em>.
          </SectionTitle>
          <p
            data-testid="patent-tech-cellphone-tag"
            className="mt-7 max-w-3xl mx-auto text-lg md:text-xl text-foreground/80 leading-relaxed"
          >
            Wearables, lab work, DNA analysis, microbiome data, and biometrics — fused into
            a personalized wellness plan. The same engine powers every{" "}
            <Link to="/wellness-optimization" className="text-primary font-semibold hover:underline">
              Integrated Optimization Program
            </Link>{" "}
            — with{" "}
            <Link to="/medical-care" className="text-primary font-semibold hover:underline">
              physician oversight
            </Link>{" "}
            built in.
          </p>

          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href={ACTIVATION_URL}
              target="_blank" rel="noopener noreferrer" data-testid="patent-tech-matrix-cta"
              className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]">
              Start Your Optimization Analysis <span aria-hidden>→</span>
            </a>
            <a  href="/digital-twin" data-testid="patent-tech-digital-twin-link"
              className="iw-btn-ghost inline-flex items-center gap-2 px-6 py-3.5 text-[0.7rem]">
              What Is a Digital Twin?
            </a>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-12">
          <div className="iw-scan-sweep relative aspect-[16/10] md:aspect-[21/10] overflow-hidden border border-border bg-foreground">
            <img
              src={PATENT_HERO}
              alt="Patent-pending clinical intelligence technology — non-invasive cell-phone scan reading heart rate, oxygen, stress, and full-body biometric signals in real time"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/45 to-transparent pointer-events-none" />
            <HudCorners offset={14} size={14} />
          </div>
        </FadeUp>

        <HRule />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {TECHS.map((t, i) => (
            <PatentTechCard key={t.name} tech={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
